import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { SignOut, CaretRight, List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import aakritiLogo from "@/assets/aakriti-logo.png";
import { NAV_TREE, STAFF_NAV_TREE, ACCOUNTANT_NAV_TREE } from "@/lib/nav";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

function NavGroup({ group, currentPath, currentSearch, onNavigate }) {
  const isInGroup = group.children.some((c) => {
    const base = c.to.split("?")[0];
    return c.end ? currentPath === base : currentPath.startsWith(base);
  }) || currentPath === group.to || currentPath.startsWith(group.to + "/");
  const [open, setOpen] = useState(isInGroup);
  useEffect(() => { if (isInGroup) setOpen(true); }, [isInGroup]);

  return (
    <div className="select-none">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${group.key}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
          isInGroup ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/[0.06] hover:text-white"
        }`}
      >
        <group.icon size={19} weight={isInGroup ? "fill" : "duotone"} className={isInGroup ? "text-accent" : ""} />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <CaretRight size={12} weight="bold" className={`shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
      </button>

      <div className={`grid transition-[grid-template-rows] duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="ml-[22px] mt-1 mb-1 border-l border-white/15 pl-3 space-y-0.5">
            {group.children.map((c) => {
              const [base, qs] = c.to.split("?");
              const active = c.end
                ? currentPath === base && (qs ? currentSearch === `?${qs}` : !currentSearch)
                : currentPath.startsWith(base) && (!qs || currentSearch === `?${qs}`);
              return (
                <NavLink
                  key={c.to}
                  to={c.to}
                  onClick={onNavigate}
                  data-testid={`nav-item-${base.replace(/\//g, "-")}${qs ? "-" + qs.replace(/=/g, "-") : ""}`}
                  className={`block px-2.5 py-[7px] rounded text-[13px] leading-tight transition-colors duration-150 ${
                    active ? "bg-accent/90 text-accent-foreground font-semibold" : "text-white/55 hover:text-white hover:bg-white/[0.06]"
                  }`}
                >
                  {c.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [perms, setPerms] = useState(null);
  useEffect(() => {
    if (user && user.role !== "owner") {
      api.get("/permissions").then((r) => setPerms(r.data?.access || null)).catch(() => setPerms(null));
    }
  }, [user?.role]);
  const tree = (() => {
    if (user?.role === "owner") return NAV_TREE;
    let base = user?.role === "accountant" ? ACCOUNTANT_NAV_TREE : STAFF_NAV_TREE;
    if (user?.role === "field_executive") {
      // Field executives capture field data only — no full report-preparation page.
      base = base.map((s) => (s.key === "my-jobs" ? { ...s, children: (s.children || []).filter((c) => c.to !== "/app/jobs") } : s));
    }
    if (!perms) return base;
    const role = user?.role;
    const keyOf = (k) => (k === "my-jobs" ? "jobs" : k);
    const can = (k) => k === "account" || k === "employees" || !!perms?.[k]?.[role];
    const baseFiltered = base.filter((s) => can(keyOf(s.key)));
    const shown = new Set(baseFiltered.map((s) => keyOf(s.key)));
    const LOCKED = new Set(["team", "records", "account", "gis"]);
    const extra = NAV_TREE.filter((s) => !shown.has(s.key) && !LOCKED.has(s.key) && can(s.key));
    return [...baseFiltered, ...extra];
  })();

  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const brand = (
    <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
      <div className="h-10 rounded-md bg-white flex items-center justify-center shrink-0 px-1.5" data-testid="brand-logo-img">
        <img src={aakritiLogo} alt="Aakriti" className="h-7 w-auto object-contain" />
      </div>
      <div className="leading-tight">
        <div className="font-display font-bold">Aakriti</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Valuation Hub</div>
      </div>
    </div>
  );

  const navBody = (
    <nav className="flex-1 overflow-y-auto p-3 space-y-0.5 sidebar-scroll">
      {tree.map((g) => (
        <NavGroup key={g.key} group={g} currentPath={location.pathname} currentSearch={location.search} onNavigate={() => setMobileOpen(false)} />
      ))}
    </nav>
  );

  const footer = (
    <div className="p-3 border-t border-white/10">
      <div className="px-3 py-2">
        <div className="text-sm font-medium truncate">{user?.name}</div>
        <div className="text-xs text-accent">{ROLE_LABELS[user?.role] || user?.role}</div>
      </div>
      <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-200">
        <SignOut size={19} /> Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-[264px] shrink-0 flex-col bg-primary text-white sticky top-0 h-screen">
        {brand}
        {navBody}
        {footer}
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[280px] max-w-[85vw] bg-primary text-white flex flex-col h-full shadow-2xl" data-testid="mobile-drawer">
            <div className="flex items-center justify-between">
              {brand}
              <button className="p-4 text-white/70" onClick={() => setMobileOpen(false)} data-testid="close-drawer"><X size={20} /></button>
            </div>
            {navBody}
            {footer}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/90 border-b border-border px-3 lg:px-6 h-14 flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-1 text-primary" data-testid="open-drawer"><List size={22} /></button>
          <div className="flex-1 min-w-0"><GlobalSearch /></div>
          <Button size="sm" variant="ghost" className="lg:hidden text-primary hover:bg-muted" onClick={doLogout} data-testid="logout-btn-mobile"><SignOut size={18} /></Button>
        </header>
        <main className="flex-1 p-5 md:p-8 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
